import { useMemo } from "react";
import { useDeleteCas3Provider } from "src/api/client-api/ssoProviders/useDeleteCas3Provider";
import { useDeleteOAuthProvider } from "src/api/client-api/ssoProviders/useDeleteOAuthProvider";
import { useDeleteSaml2Provider } from "src/api/client-api/ssoProviders/useDeleteSaml2Provider";
import { useGetAllCas3Providers } from "src/api/client-api/ssoProviders/useGetAllCas3Providers";
import { useGetAllOAuthProviders } from "src/api/client-api/ssoProviders/useGetAllOAuthProviders";
import { useGetAllSaml2Providers } from "src/api/client-api/ssoProviders/useGetAllSaml2Providers";
import { useUpsertCas3Provider } from "src/api/client-api/ssoProviders/useUpsertCas3Provider";
import { useUpsertOAuthProvider } from "src/api/client-api/ssoProviders/useUpsertOAuthProvider";
import { useUpsertSaml2Provider } from "src/api/client-api/ssoProviders/useUpsertSaml2Provider";
import { SsoProtocol, SSO_PROTOCOLS } from "src/pages/Authentication/ssoConnector";
import { buildProviderPayload, ProviderFormValues } from "./ssoProviderModel";

export type SsoProviderListItem = {
    protocol: SsoProtocol;
    id: number;
    providerName: string;
    raw: Record<string, unknown>;
};

type ProviderLike = { id: number; providerName: string };

export const toListItems = (protocol: SsoProtocol, providers: ProviderLike[] | undefined): SsoProviderListItem[] =>
    (providers ?? []).map((provider) => ({
        protocol,
        id: provider.id,
        providerName: provider.providerName,
        raw: provider as unknown as Record<string, unknown>,
    }));

export const combineSsoProviders = (
    byProtocol: Record<SsoProtocol, ProviderLike[] | undefined>,
): SsoProviderListItem[] =>
    SSO_PROTOCOLS.flatMap((protocol) => toListItems(protocol, byProtocol[protocol])).sort((left, right) =>
        left.providerName.localeCompare(right.providerName),
    );

export const useSsoProviders = () => {
    const oauth = useGetAllOAuthProviders();
    const saml2 = useGetAllSaml2Providers();
    const cas3 = useGetAllCas3Providers();

    const upsertOAuth = useUpsertOAuthProvider();
    const upsertSaml2 = useUpsertSaml2Provider();
    const upsertCas3 = useUpsertCas3Provider();

    const deleteOAuth = useDeleteOAuthProvider();
    const deleteSaml2 = useDeleteSaml2Provider();
    const deleteCas3 = useDeleteCas3Provider();

    const isLoading = oauth.isLoading || saml2.isLoading || cas3.isLoading;

    const providers: SsoProviderListItem[] | undefined = useMemo(
        () =>
            isLoading ? undefined : (
                combineSsoProviders({
                    oauth: oauth.oauthProviders,
                    saml2: saml2.saml2Providers,
                    cas3: cas3.cas3Providers,
                })
            ),
        [isLoading, oauth.oauthProviders, saml2.saml2Providers, cas3.cas3Providers],
    );

    const failedProtocols = SSO_PROTOCOLS.filter(
        (protocol) => ({ oauth: oauth.isError, saml2: saml2.isError, cas3: cas3.isError })[protocol],
    );

    const upsertByProtocol = { oauth: upsertOAuth, saml2: upsertSaml2, cas3: upsertCas3 };
    const deleteByProtocol = { oauth: deleteOAuth, saml2: deleteSaml2, cas3: deleteCas3 };

    return {
        providers,
        failedProtocols,
        isLoading,
        isSaving: upsertOAuth.isLoading || upsertSaml2.isLoading || upsertCas3.isLoading,
        isDeleting: deleteOAuth.isLoading || deleteSaml2.isLoading || deleteCas3.isLoading,
        saveProvider: (protocol: SsoProtocol, id: number | undefined, values: ProviderFormValues) =>
            upsertByProtocol[protocol].mutateAsync({ id, payload: buildProviderPayload(protocol, values) }),
        deleteProvider: (item: SsoProviderListItem) => deleteByProtocol[item.protocol].mutateAsync(item.id),
    };
};
